import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

export interface ArticleSection {
  id: string;
  title: string;
  paragraphs: string[];
  table?: {
    headers: string[];
    rows: string[][];
  };
  listItems?: string[];
  cards?: { title: string; text: string; icon: string }[];
}

export interface ArticleData {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  publishedDate: string;
  modifiedDate: string;
  readTime: string;
  featuredImage: string;
  featuredImageAlt: string;
  intro: string;
  sections: ArticleSection[];
  tags: string[];
}

interface ArticleLayoutProps {
  article: ArticleData;
}

const siteUrl = 'https://itaobuyspread.boats';
const logoUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg';

function buildArticleSchema(article: ArticleData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.featuredImage,
    datePublished: article.publishedDate,
    dateModified: article.modifiedDate,
    author: {
      '@type': 'Organization',
      name: 'Itaobuy Spread',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Itaobuy Spread',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/articoli/${article.slug}`,
    },
  };
}

function buildBreadcrumbSchema(article: ArticleData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Articoli',
        item: `${siteUrl}/#articoli`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `${siteUrl}/articoli/${article.slug}`,
      },
    ],
  };
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <JsonLd data={buildArticleSchema(article)} />
      <JsonLd data={buildBreadcrumbSchema(article)} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-4">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to="/#articoli" className="hover:text-primary-600 transition-colors">Articoli</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{article.title.substring(0, 60)}...</span>
          </nav>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-xs font-semibold bg-accent-100 text-accent-700 px-3 py-1 rounded-full">{article.category}</span>
              <span className="text-xs text-foreground-400">Aggiornato: {article.modifiedDate}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-6 leading-tight">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
              {article.intro}
            </p>
          </header>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.featuredImage}
              alt={article.featuredImageAlt}
              className="w-full h-[300px] md:h-[450px] object-cover"
            />
          </div>

          <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-12">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">Indice dei Contenuti</h2>
            <ol className="space-y-2">
              {article.sections.map((section, i) => (
                <li key={section.id} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors">
                  <a href={`#${section.id}`}>{i + 1}. {section.title}</a>
                </li>
              ))}
            </ol>
          </div>

          {article.sections.map((section) => (
            <section key={section.id} id={section.id} className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                {section.title}
              </h2>

              {section.paragraphs.map((p, pi) => (
                <p key={pi} className="text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }}></p>
              ))}

              {section.table && (
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        {section.table.headers.map((h, hi) => (
                          <th key={hi} className={`text-left py-3 px-4 text-foreground-800 font-semibold ${hi === 0 ? 'rounded-tl-lg' : ''} ${hi === section.table!.headers.length - 1 ? 'rounded-tr-lg' : ''}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.rows.map((row, ri) => (
                        <tr key={ri} className="border-b border-background-200/70">
                          {row.map((cell, ci) => (
                            <td key={ci} className="py-3 px-4 text-foreground-600">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.listItems && (
                <ul className="space-y-3 mb-6">
                  {section.listItems.map((item, li) => (
                    <li key={li} className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mt-0.5">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item }}></p>
                    </li>
                  ))}
                </ul>
              )}

              {section.cards && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {section.cards.map((card, ci) => (
                    <div key={ci} className="bg-background-100 rounded-xl p-5">
                      <h4 className="font-semibold text-foreground-900 mb-2">
                        <i className={`${card.icon} text-primary-500 mr-2`}></i>
                        {card.title}
                      </h4>
                      <p className="text-sm text-foreground-500">{card.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}

          <section className="mb-12">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Esplora i prodotti su kaspreadsheet.com
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-4 border-2 border-foreground-200 text-foreground-700 font-semibold text-sm rounded-full hover:border-foreground-400 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Torna alla Home
              </Link>
            </div>
          </section>

          <div className="border-t border-background-200/70 pt-8 mt-12">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs text-foreground-400">Tag:</span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-3 py-1 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}